import { clearWidgetAuthLaunchCookie } from '@/app-shell/widget-auth/widget-auth-launch-cookie';
import { useMutateSwitchLinkedAccountToNewUser } from '@/hooks/mutation/integration/widget/useMutateSwitchLinkedAccountToNewUser';
import { useMutateUnlinkWidgetAccount } from '@/hooks/mutation/integration/widget/useMutateUnlinkWidgetAccount';
import { useQueryUser } from '@/hooks/query/useQueryUser';
import { widgetLinkAccountUnlinkSwitch } from '@/lib/analytics/scribe/integrations';
import { logout } from '@/lib/authActions';
import { toast } from '@heidi/design-system/toast';
import {
  clearIntegrationAuth,
  integrationNameAtom,
  widgetRegionAtom,
  widgetTokenAtom,
} from '@heidi/integrations/ehr/data/atoms';
import { useAtomValue } from 'jotai';
import { useRouter } from 'next/router';
import { useCallback } from 'react';
import { useIntl } from 'react-intl';

export const useWidgetOnboarding = ({
  onDismiss,
}: {
  onDismiss?: () => void;
} = {}) => {
  const router = useRouter();
  const intl = useIntl();

  const { mutateAsync: unlinkWidgetAccount } = useMutateUnlinkWidgetAccount();
  const { mutateAsync: switchLinkedAccount, isPending: isSwitchingAccount } =
    useMutateSwitchLinkedAccountToNewUser();
  const { data: userData } = useQueryUser((data) => ({
    kindeUserId: data.kinde_user_id,
  }));
  const widgetToken = useAtomValue(widgetTokenAtom);
  const integrationName = useAtomValue(integrationNameAtom);
  const widgetRegion = useAtomValue(widgetRegionAtom);

  const dismiss = useCallback(() => {
    onDismiss?.();
    clearWidgetAuthLaunchCookie();
    clearIntegrationAuth();
    setTimeout(() => void router.push('/'), 500);
  }, [router, onDismiss]);

  const unlinkAndSwitchAccounts = async () => {
    await unlinkWidgetAccount();

    const params = new URLSearchParams();
    if (widgetToken) params.set('t', widgetToken);
    if (integrationName) params.set('productName', integrationName);
    if (widgetRegion) params.set('region', widgetRegion);
    params.set('reset', 'true');

    widgetLinkAccountUnlinkSwitch({ integrationName });

    void logout({
      postLogoutRedirectURL: `/integration/widget/auth?${params.toString()}`,
    });
  };

  const switchToCurrentAccount = useCallback(
    async ({ onSuccess }: { onSuccess?: () => void } = {}) => {
      if (!userData?.kindeUserId) return;

      try {
        await switchLinkedAccount({ newKindeUserId: userData.kindeUserId });
        widgetLinkAccountUnlinkSwitch({ integrationName });
        onSuccess?.();
      } catch {
        toast({
          title: intl.formatMessage({
            id: 'zJMmNvMQWr',
            defaultMessage: 'Failed to switch account, please try again',
          }),
          tone: 'destructive',
        });
      }
    },
    [userData?.kindeUserId, switchLinkedAccount, integrationName, intl]
  );

  return {
    dismiss,
    unlinkAndSwitchAccounts,
    switchToCurrentAccount,
    isSwitchingAccount,
  };
};
